import { inject } from '@angular/core';
import type { Route, Routes } from '@angular/router';
import { permissionGuard } from '../../../../core/guards/permission.guard';
import { PermissionService } from '../../../../core/services/permission.service';
import { COMMUNICATIONS_SECTION_LIST, communicationsLandingUrl } from './shell/communications-sections';
import { CommsLookupStore } from './shared/state/comms-lookup.store';
import { CommunicationsContextStore } from './shared/state/communications-context.store';

/** Every Communications page is re-checked on in-area navigation (the Benefits / Tasks rule); redirects are left alone. */
export function withCommunicationsPermissionGuard(routes: Routes): Routes {
  return routes.map((route): Route =>
    route.loadComponent || route.component ? { ...route, canActivate: [...(route.canActivate ?? []), permissionGuard] } : route,
  );
}

export const COMMUNICATIONS_ROUTE: Route = {
  path: 'communications',
  // One instance per visit, shared by every Communications page; reset when the user leaves /communications.
  providers: [CommunicationsContextStore, CommsLookupStore],
  loadComponent: () => import('./shell/communications-shell.component').then((m) => m.CommunicationsShellComponent),
  children: withCommunicationsPermissionGuard([
    {
      path: '',
      pathMatch: 'full',
      redirectTo: () => {
        const permissions = inject(PermissionService);
        return communicationsLandingUrl((anyOf) => permissions.hasAny(anyOf), inject(COMMUNICATIONS_SECTION_LIST));
      },
    },
    {
      // SP3b T2: every post by status / kind / search.
      path: 'posts',
      loadComponent: () => import('./posts/list/posts-list-page.component').then((m) => m.PostsListPageComponent),
    },
    {
      // SP3b T5: the editor; `?kind=1..4` picks the kind of a new post. Declared before `posts/:id` so "new" is no id.
      path: 'posts/new',
      loadComponent: () => import('./posts/editor/post-editor-page.component').then((m) => m.PostEditorPageComponent),
    },
    {
      path: 'posts/:id/edit',
      loadComponent: () => import('./posts/editor/post-editor-page.component').then((m) => m.PostEditorPageComponent),
    },
    {
      // SP3b T3: one post with its workflow actions and attachments.
      path: 'posts/:id',
      loadComponent: () => import('./posts/detail/post-detail-page.component').then((m) => m.PostDetailPageComponent),
    },
    {
      // SP3c T1: module-only (R26).
      path: 'approvals',
      loadComponent: () => import('./approvals/approvals-page.component').then((m) => m.ApprovalsPageComponent),
    },
    {
      // SP3c T2: report queue + employee posts waiting for review (Moderate or Manage).
      path: 'moderation',
      loadComponent: () => import('./moderation/moderation-page.component').then((m) => m.ModerationPageComponent),
    },
    {
      // SP3a T4: list, All / Active filter, add / edit / delete (writes need Settings.Manage or Manage, R9).
      path: 'categories',
      loadComponent: () => import('./categories/categories-page.component').then((m) => m.CategoriesPageComponent),
    },
    {
      // SP3c T3: engagement per kind and per post (Reports.View or Manage).
      path: 'reports',
      loadComponent: () => import('./reports/engagement-page.component').then((m) => m.EngagementPageComponent),
    },
    {
      path: 'reports/posts/:id',
      loadComponent: () => import('./reports/post-report-page.component').then((m) => m.PostReportPageComponent),
    },
    {
      // SP3a T5: the tenant's settings document (Settings.Manage or Manage, R9).
      path: 'settings',
      loadComponent: () => import('./settings/communications-settings-page.component').then((m) => m.CommunicationsSettingsPageComponent),
    },
  ]),
};
